import { useEffect, useState } from 'react';
import { adminApi, createAdminRequest, type AdminRequestKind, type AdminTeamMember } from '@/lib/adminApi';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Send } from 'lucide-react';

export const REQUEST_KINDS: { key: AdminRequestKind; label: string; hint: string }[] = [
  { key: 'approval', label: 'Approval', hint: 'You need a yes or no before you go ahead.' },
  { key: 'decision', label: 'Decision', hint: 'There are options and a director should choose.' },
  { key: 'question', label: 'Question', hint: 'You need an answer or some guidance.' },
  { key: 'access', label: 'Access', hint: 'You need a page or action your account cannot open.' },
  { key: 'other', label: 'Something else', hint: 'Anything the directors should know about.' },
];

const ANY_DIRECTOR = 'any';

export interface NewRequestPrefill {
  kind?: AdminRequestKind;
  subject?: string;
  body?: string;
  assignedTo?: string | null;
  pagePath?: string | null;
}

/**
 * Form for raising a request to the directors. Staff can address it to one
 * director or leave it for whichever director picks it up first.
 */
export default function NewRequestDialog({
  open,
  onOpenChange,
  prefill,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefill?: NewRequestPrefill;
  onCreated?: (id: string) => void;
}) {
  const { user, isManager } = useAdminAuth();
  const [kind, setKind] = useState<AdminRequestKind>('question');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [assignedTo, setAssignedTo] = useState<string>(ANY_DIRECTOR);
  const [recipients, setRecipients] = useState<AdminTeamMember[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKind(prefill?.kind ?? 'question');
    setSubject(prefill?.subject ?? '');
    setBody(prefill?.body ?? '');
    setAssignedTo(prefill?.assignedTo ?? ANY_DIRECTOR);
    adminApi.getAdminTeam()
      .then(({ members }) => setRecipients(members.filter((m) =>
        m.status === 'active'
        && m.user_id !== user?.id
        && (m.role === 'director' || (isManager && m.role === 'owner')),
      )))
      .catch(() => setRecipients([]));
    // Prefill is read once each time the dialog opens, so typing is never reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const kindHint = REQUEST_KINDS.find((k) => k.key === kind)?.hint;
  const canSend = subject.trim().length >= 3 && body.trim().length > 0 && !sending;

  const send = async () => {
    setSending(true);
    try {
      const id = await createAdminRequest({
        kind,
        subject: subject.trim(),
        body: body.trim(),
        assignedTo: assignedTo === ANY_DIRECTOR ? null : assignedTo,
        pagePath: prefill?.pagePath ?? null,
      });
      toast.success('Request sent. You will see the reply in Requests.');
      onOpenChange(false);
      window.dispatchEvent(new Event('admin-requests-changed'));
      onCreated?.(id);
    } catch (err: any) {
      toast.error(err.message || 'Could not send the request');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>New request</DialogTitle>
          <DialogDescription className="text-gray-400">
            {isManager
              ? 'Raise something with another director. It is kept with the rest of the team requests.'
              : 'Goes to the directors. Only you, the directors and the owner can see it.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-gray-300">Kind</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as AdminRequestKind)}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700 text-white">
                  {REQUEST_KINDS.map((k) => (
                    <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Send to</Label>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700 text-white">
                  <SelectItem value={ANY_DIRECTOR}>Any director</SelectItem>
                  {recipients.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.display_name}{m.role === 'owner' ? ' (Owner)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {kindHint && <p className="text-xs text-gray-500 -mt-2">{kindHint}</p>}

          <div className="space-y-2">
            <Label className="text-gray-300">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={120}
              placeholder="For example: 10% discount for Fort Young Hotel"
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">Details</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={4000}
              rows={6}
              placeholder="What do you need, and by when? Add anything that helps a director decide quickly."
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>
          {prefill?.pagePath && (
            <p className="text-xs text-gray-500">Linked page: <span className="font-mono text-gray-400">{prefill.pagePath}</span></p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-gray-400" disabled={sending}>
            Cancel
          </Button>
          <Button onClick={send} disabled={!canSend} className="bg-emerald-600 hover:bg-emerald-500 text-white">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
